import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggingService } from '../../services/logging.service';
import { Toro } from '../common/enumerations';
import { ImageManagerService } from '../managers/image-manager.service';
import { LocalCacheManager } from '../managers/local-cache.manager';
import { AuxiliaryEditModel } from '../models/auxiliary-edit.model';
import { FireAndWaitForNotificationCommandResponse } from '../models/command-responses/fire-and-wait-for-notification-command-response.model';
import { WaitUntilCompleteCommandResponse } from '../models/command-responses/wait-until-complete-command-response.model';
import { ControllerStatus } from '../models/controller-status.model';
import { EvoControllerBundle } from '../models/evo-controller-bundle.model';
import { ControllerInfoForConfigDisplay } from '../models/irrigation/ControllerInfoForConfigDisplay.model';
import DeviceType = Toro.DeviceType;
import { Kd2ControllerBundle } from '../models/kd2-controller-bundle.model';
import { ControllerProgrammability } from '../models/programmability-ux/controller-programmability.model';
import { RDControllerBundle } from '../models/rd-controller-bundle.model';
import { AddControllerModel } from '../models/toro-add-controller.model';
import { ToroControllerBundle } from '../models/toro-controller-bundle';
import { ToroControllerConfiguration } from '../models/toro-controller-configuration.model';
import RunningMode = Toro.RunningMode;
import { ControllerDefinition } from '../models/toro-controller-definition.model';
import { EditControllerModel } from '../models/toro-edit-controller.model';
import { ToroIrrigationCrudResponseModel } from '../models/toro-irrigation-crud-response.model';
import { ToroStationEdit } from '../models/toro-station-edit.model';
import { ToroStation } from '../models/toro-station.model';
import { ApiUrlService } from './api-url.service';
import { ApiService } from './api.service';
import { LXCControllerBundle } from '../models/lxc-controller-bundle.model';

@Injectable()
export class ControllerApiService extends ApiService {
    constructor(protected http: HttpClient,
        protected apiUrlService: ApiUrlService,
        protected imageManager: ImageManagerService,
        protected localCacheManager: LocalCacheManager) {
        super(http, localCacheManager);
    }

    getControllersForLocation(locationId: number): Observable<ToroControllerBundle[]> {
        const controllerBundles: ToroControllerBundle[] = [];

        return this.apiRequest<ToroControllerBundle>(this.apiUrlService.getControllersForLocationUrl(locationId), this.HttpMethod.Get)
            .pipe(
                map(response => {
                    if (response && response['Data']) {
                        //TODO LXC
                        response['Data']['Controllers'].forEach(controllerBundle => {
                                controllerBundles.push(this.mapControllerToSpecificType(controllerBundle, controllerBundle.Controller.DeviceTypeId));
                        });
                    }


                    return controllerBundles;
                })
            );
    }

    getControllersListForLocation(gatewayId: number, locationId: number): Observable<ToroControllerBundle[]> {
        const controllerBundles: ToroControllerBundle[] = [];

        return this.apiRequest<ToroControllerBundle>(this.apiUrlService.getControllersListForLocationUrl(locationId, gatewayId), this.HttpMethod.Get)
            .pipe(
                map(response => {
                    // if (response && response['Data']) {
                    //     response['Data']['Controllers'].forEach(controllerBundle => {
                    //         controllerBundles.push(this.mapControllerToSpecificType(controllerBundle, controllerBundle.Controller.DeviceTypeId));
                    //     });
                    // }

                    if (response && response['Controllers']) {
                        response['Controllers'].forEach(controllerBundle => {
                            controllerBundles.push(this.mapControllerToSpecificType(controllerBundle, controllerBundle.Controller.DeviceTypeId));
                        });
                    }
                    return controllerBundles;
                })
            );
    }

    /* getControllerDetails(locationId: number, controllerId: number): Observable<ToroControllerBundle> {
        return this.apiRequest<ToroControllerBundle>(this.apiUrlService.getControllerDetailsUrl(locationId, controllerId), this.HttpMethod.Get)
            .pipe(
                map(response => {
                    const controller = new ToroControllerBundle(response['Controllers'][0], this.imageManager);
                    if (!controller) {
                        return new ToroControllerBundle(response['Controllers'][0], this.imageManager);
                    }
                    return this.mapControllerToSpecificType(response['Controllers'][0], controller.Controller.DeviceTypeId);
                })
            );
    } */

    getControllerDetails(locationId: number, controllerId: number): Observable<ToroControllerBundle | any> {
        return this.apiRequest<ToroControllerBundle>(this.apiUrlService.getControllerDetailsUrl(locationId, controllerId), this.HttpMethod.Get)
            .pipe(
                map(response => {
                    let controllerData;
                    if(response && response['success'] == true){
                        controllerData = response && response['data'] ? response['data'] : null;
                        const controller = new ToroControllerBundle(controllerData, this.imageManager);
                        if (!controller) {
                            return new ToroControllerBundle(controllerData, this.imageManager);
                        }
                        return this.mapControllerToSpecificType(controllerData, controller.Controller.DeviceTypeId);
                    } else {
                        LoggingService.log(response['message']);
                        throw new Error(response['error']);
                    }
                })
            );
    }

    mapControllerToSpecificType(json: object, deviceType: DeviceType) {
        switch (deviceType) {
            case DeviceType.KD2:
                return new Kd2ControllerBundle(json, this.imageManager);
            case DeviceType.RD:
                return new RDControllerBundle(json, this.imageManager);
            case DeviceType.EVO:
                return new EvoControllerBundle(json, this.imageManager);
            case DeviceType.LXCEXP:
                return new LXCControllerBundle(json, this.imageManager);
            default:
                return new ToroControllerBundle(json, this.imageManager);
        }
    }

    startStation(controllerId: number, stationNumber: number, duration: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getStartStationUrl(controllerId, stationNumber, duration), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response.WasSuccess;
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    startProgram(controllerId: number, programNumber: number): Observable<boolean> {

        return this.apiRequest(this.apiUrlService.getStartProgramUrl(controllerId, programNumber), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response.WasSuccess;
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    startAuxiliary(controllerId: number, auxiliaryNumber: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getStartAuxiliaryUrl(controllerId, auxiliaryNumber), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response['Success'];
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    stopControllerIrrigation(controllerId: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getStopAllIrrigationforControllerUrl(controllerId), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response.WasSuccess;
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    stopStationIrrigation(controllerId: number, stationNumber:number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getStopStationIrrigationUrl(controllerId,stationNumber), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response.WasSuccess;
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    stopProgramIrrigation(controllerId: number, programNumber:number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getStopProgramIrrigationUrl(controllerId,programNumber), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response.WasSuccess;
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    getControllerStatus(controllerId: number): Observable<ControllerStatus> {
        return this.apiRequest(this.apiUrlService.getControllerStatusUrl(controllerId), this.HttpMethod.Get)
            .pipe(
                map((response: ControllerStatus) => new ControllerStatus(response))
            );
    }

    getControllerRunningStatus(controllerId: number): Observable<{ controllerId: number, sentSuccessfully: boolean }> {
        return this.apiRequest<FireAndWaitForNotificationCommandResponse>(this.apiUrlService.getControllerRunningStatusUrl(controllerId), this.HttpMethod.Get)
            .pipe(
                map((response: FireAndWaitForNotificationCommandResponse) => {
                    return { controllerId: controllerId, sentSuccessfully: response.MessageSentSuccessfully };
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of({ controllerId: controllerId, sentSuccessfully: false, error: error });
                })
            );
    }

    setRunningMode(controllerId: number, runningMode: RunningMode, daysToRemainOff: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getRunningModeUrl(controllerId, runningMode, daysToRemainOff), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response.WasSuccess;
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    loadControllerTypes(): Observable<ControllerDefinition[]> {
        return this.apiRequest(this.apiUrlService.getControllerTypesUrl(), this.HttpMethod.Get);
    }

    addController(locationId: number, addControllerModel: AddControllerModel): Observable<ToroIrrigationCrudResponseModel> {
        return this.apiRequest(this.apiUrlService.getAddControllerUrl(locationId), this.HttpMethod.Post, JSON.stringify(addControllerModel));
    }

    editController(controllerId: number, editControllerModel: EditControllerModel): Observable<ToroIrrigationCrudResponseModel> {
        return this.apiRequest(this.apiUrlService.getEditControllerUrl(controllerId), this.HttpMethod.Put, JSON.stringify(editControllerModel));
    }

    editStation(controllerId: number, stationNumber: number, station: ToroStationEdit): Observable<ToroStation> {
        return this.apiRequest(this.apiUrlService.getUpdateStationUrl(controllerId, stationNumber), this.HttpMethod.Put, station);
    }

    editAuxiliary(controllerId: number, auxiliaryNumber: number, auxiliary: AuxiliaryEditModel): Observable<any> {
        return this.apiRequest(this.apiUrlService.getUpdateAuxiliaryUrl(controllerId, auxiliaryNumber), this.HttpMethod.Put, auxiliary);
    }

    replaceCommCard(controllerId: number, deviceId: string, controllerTypeId: number, pin?: number) {
        return this.apiRequest(this.apiUrlService.getReplaceCommCardUrl(controllerId, deviceId, controllerTypeId, pin), this.HttpMethod.Post);
    }

    sendRecordsToController(controllerId: number): Observable<FireAndWaitForNotificationCommandResponse> {
        return this.apiRequest(this.apiUrlService.getSendRecordsToControllerUrl(controllerId), this.HttpMethod.Get)
            .pipe(
                map((response: FireAndWaitForNotificationCommandResponse) => {
                    return new FireAndWaitForNotificationCommandResponse(response);
                })
            );
    }

    getControllerRecords(controllerId: number, forceGetFromController = false): Observable<FireAndWaitForNotificationCommandResponse> {
        return this.apiRequest(this.apiUrlService.getControllerRecordsUrl(controllerId, forceGetFromController), this.HttpMethod.Get)
            .pipe(
                map((response: FireAndWaitForNotificationCommandResponse) => {
                    return new FireAndWaitForNotificationCommandResponse(response);
                })
            );
    }

    getControllerConfigDisplayInfo(controllerId: number): Observable<ControllerInfoForConfigDisplay> {
        return this.apiRequest(this.apiUrlService.getControllerConfigDisplayInfoUrl(controllerId), this.HttpMethod.Get)
            .pipe(
                map((controllerInfo: ControllerInfoForConfigDisplay) => {
                    return new ControllerInfoForConfigDisplay(controllerInfo);
                })
            );

    }

    resyncCommunicationCard(controllerId: number): Observable<WaitUntilCompleteCommandResponse> {
        return this.apiRequest(this.apiUrlService.getResyncCommunicationCardUrl(controllerId), this.HttpMethod.Get)
            .pipe(
                map((response: WaitUntilCompleteCommandResponse) => {
                    return new WaitUntilCompleteCommandResponse(response);
                })
            );
    }

    deleteIrrigationController(controllerId: number) {
        return this.apiRequest(this.apiUrlService.getDeleteControllerUrl(controllerId), this.HttpMethod.Delete);
    }

    exportConfiguration(controllerId: number): Observable<Blob> {
        return this.apiRequestToGetBlob(this.apiUrlService.getExportConfigurationUrl(controllerId), this.HttpMethod.Post);
    }

    importConfiguration(controllerId: number, data: FormData): Observable<{ ControllerConfig: ToroControllerConfiguration, Success: boolean }> {
        return this.apiRequest(this.apiUrlService.getImportConfigurationUrl(controllerId), this.HttpMethod.Post, data);
    }

    getControllerProgrammabilityUx(controllerId: number): Observable<ControllerProgrammability> {
        return this.apiRequest(this.apiUrlService.getControllerProgrammabilityUxUrl(controllerId), this.HttpMethod.Get)
            .pipe(
                map(response => new ControllerProgrammability(response))
            );
    }

}
